export type SpeechFallback = 'first-sentence' | 'full' | 'skip';

export interface ResolveSpeechInput {
  fullText: string;
  summary: string | null;
  enabled: boolean;
  fallback: SpeechFallback;
}

/**
 * Decide what text (if any) should be spoken for an assistant turn.
 * Returns null when nothing should be spoken.
 *
 * Priority:
 *   1. If the feature is disabled, speak the full reply (Stage 8 behaviour).
 *   2. Else if the model emitted a <speech> summary, speak it.
 *   3. Else consult fallback: 'first-sentence' | 'full' | 'skip'.
 */
export function resolveSpeechText(input: ResolveSpeechInput): string | null {
  const full = input.fullText.trim();
  if (!full) return null;

  if (!input.enabled) return full;

  const summary = input.summary?.trim() ?? '';
  if (summary.length > 0) return summary;

  switch (input.fallback) {
    case 'skip':
      return null;
    case 'full':
      return full;
    case 'first-sentence':
    default:
      return firstSentenceOf(full);
  }
}

/**
 * Extract the first sentence (terminated by `.`, `!`, `?` + whitespace/EOL)
 * or the first 25 words when the text is punctuation-free.
 */
export function firstSentenceOf(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return '';
  const match = trimmed.match(/^(.*?[.!?])(\s|$)/);
  const firstSentence = match ? match[1] : trimmed;
  const words = firstSentence.split(/\s+/);
  if (words.length <= 25) return firstSentence;
  return words.slice(0, 25).join(' ') + '…';
}
